import CompanionLogo from "@assets/imgs/companion.png";
import CompanionHeads from "@assets/imgs/Companions-sidebar.png";
import {
  faHistory,
  faGear,
  faGripLinesVertical,
} from "@fortawesome/free-solid-svg-icons";
import {
  faMessageSmile,
  faQuestion,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth } from "@hooks";
import {
  AvailableCompanionTabs,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_DEFAULT_WIDTH,
  SIDEBAR_MIN_WIDTH,
  useCompanion,
} from "@hooks/use-companion";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useRef, useState } from "react";

import { CompanionNavButton, NewSessionButton } from "./Buttons";
import CompanionConsentModal from "./CompanionConsentModal";
import CompanionHelp from "./CompanionHelp";
import { CompanionModalContainerProvider } from "./CompanionModalContainerContext";
import { CompanionSessionChat } from "./CompanionSessionChat";
import { CompanionSessionHistory } from "./CompanionSessionHistory";
import CompanionSettings from "./CompanionSettings";

export const CompanionSidebar = () => {
  const SNAP_DISTANCE = 128; // snap to min width if the drag lands near edge

  // State
  const [dragging, setDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(null);
  const [containerElement, setContainerElement] = useState(null);

  // Refs
  const handleRef = useRef(null); // the draggable grip
  const dragOccurredRef = useRef(false);

  const {
    companionSession,
    activeTab,
    setActiveTab,
    collapseSidebar,
    sidebarWidth: width,
    setSidebarWidth: setWidth,
    creatingSession,
  } = useCompanion();
  const { billingCompanion, companion } = useFlags();
  const { isUserAuthenticated } = useAuth();
  const isCollapsed = width <= SIDEBAR_MIN_WIDTH;

  const onHandlePointerDown = (e) => {
    dragOccurredRef.current = false;
    setDragStartX(e.clientX);
  };

  // Effects
  useEffect(() => {
    const onWindowResize = () => {
      setWidth((w) =>
        Math.max(
          SIDEBAR_COLLAPSED_WIDTH,
          Math.min(w, window.innerWidth),
        ),
      );
    };

    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, [setWidth]);

  useEffect(() => {
    if (!dragging && !dragStartX) return;

    const onDragMove = (ev) => {
      if (!containerElement) return;
      ev.preventDefault();
      const left = containerElement.getBoundingClientRect().left || 0;
      setWidth(
        Math.max(
          SIDEBAR_COLLAPSED_WIDTH,
          Math.min(ev.clientX - left, window.innerWidth),
        ),
      );
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
    };

    const onDragEnd = () => {
      if (dragging) {
        dragOccurredRef.current = true;
        setWidth((current) => {
          let snap = current;
          if (current <= SIDEBAR_MIN_WIDTH + SNAP_DISTANCE) {
            snap = SIDEBAR_COLLAPSED_WIDTH;
          } else if (
            document.body.clientWidth < document.body.scrollWidth
          ) {
            snap = window.innerWidth;
          }
          return snap;
        });
      }

      setDragging(false);
      setDragStartX(null);

      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };

    const onHandlePointerMove = (e) => {
      if (dragStartX === null) return;

      const deltaX = Math.abs(e.clientX - dragStartX);
      if (deltaX > 5 && !dragging) {
        setDragging(true);
        handleRef.current?.setPointerCapture?.(e.pointerId);
      }
    };

    document.addEventListener("pointermove", onDragMove, {
      passive: false,
    });
    document.addEventListener("pointermove", onHandlePointerMove);
    document.addEventListener("pointerup", onDragEnd);
    document.addEventListener("pointercancel", onDragEnd);

    return () => {
      document.removeEventListener("pointermove", onDragMove);
      document.removeEventListener(
        "pointermove",
        onHandlePointerMove,
      );
      document.removeEventListener("pointerup", onDragEnd);
      document.removeEventListener("pointercancel", onDragEnd);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [dragging, dragStartX, width, containerElement, setWidth]);

  useEffect(() => {
    if (
      activeTab === AvailableCompanionTabs.chat &&
      !companionSession?.companionSessionUuid
    ) {
      setActiveTab(AvailableCompanionTabs.history);
    }
  }, [
    companionSession?.companionSessionUuid,
    activeTab,
    setActiveTab,
  ]);

  useEffect(() => {
    if (width >= window.innerWidth || dragging) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [width, dragging]);

  if (!isUserAuthenticated) {
    return null;
  }

  const containerBase =
    "h-screen bg-indigo-600 p-4 flex flex-col font-companion text-md shrink-0";
  const positionClass = "sticky top-0";
  const transitionClass =
    !dragging && "transition-[width] duration-150 ease-out";

  return (
    <CompanionModalContainerProvider value={containerElement}>
      <div
        ref={setContainerElement}
        className={`${containerBase} ${positionClass} ${transitionClass} relative ${isCollapsed ? "cursor-pointer" : ""}`}
        style={{ width: `${width}px` }}
        onClick={
          isCollapsed
            ? () => setWidth(SIDEBAR_DEFAULT_WIDTH)
            : undefined
        }
        data-testid="companion-sidebar"
      >
        <div className="flex justify-between items-center">
          {isCollapsed ? (
            <>
              <img
                src={CompanionHeads}
                width="45px"
                className={"absolute top-2 left-2 z-50 max-w-max"}
              />
            </>
          ) : (
            <>
              <div className="flex flex-row items-center gap-2">
                <img
                  src={CompanionLogo}
                  width="140px"
                  className="py-2"
                />
                <div className="text-white text-sm bg-cyan-500 px-2 py-1 rounded-full">
                  Beta
                </div>
              </div>
              <div className="flex gap-2">
                {companionSession?.companionSessionUuid && (
                  <CompanionNavButton
                    icon={faMessageSmile}
                    title="Active Chat"
                    isTabActive={
                      activeTab === AvailableCompanionTabs.chat
                    }
                    onClick={() =>
                      setActiveTab(AvailableCompanionTabs.chat)
                    }
                    data-testid="companion-active-session"
                  />
                )}
                <CompanionNavButton
                  icon={faHistory}
                  title="Chat History"
                  isTabActive={
                    activeTab === AvailableCompanionTabs.history
                  }
                  onClick={() =>
                    setActiveTab(AvailableCompanionTabs.history)
                  }
                  data-testid="companion-session-history"
                />
                {billingCompanion && (
                  <CompanionNavButton
                    icon={faGear}
                    title="Settings"
                    isTabActive={
                      activeTab === AvailableCompanionTabs.settings
                    }
                    onClick={() =>
                      setActiveTab(AvailableCompanionTabs.settings)
                    }
                    data-testid="companion-settings-button"
                  />
                )}
                <CompanionNavButton
                  icon={faQuestion}
                  title="Help"
                  isTabActive={
                    activeTab === AvailableCompanionTabs.help
                  }
                  onClick={() =>
                    setActiveTab(AvailableCompanionTabs.help)
                  }
                />
              </div>
            </>
          )}
        </div>

        <div
          className={`${
            isCollapsed && "invisible max-w-0"
          } flex flex-col flex-1 relative overflow-hidden`}
        >
          <div className="overflow-auto flex-1 w-full">
            {companion ? (
              <>
                {activeTab === AvailableCompanionTabs.chat && (
                  <CompanionSessionChat loading={creatingSession} />
                )}
                {activeTab === AvailableCompanionTabs.history && (
                  <CompanionSessionHistory />
                )}
                {activeTab === AvailableCompanionTabs.settings && (
                  <CompanionSettings />
                )}
                {activeTab === AvailableCompanionTabs.help && (
                  <CompanionHelp />
                )}
              </>
            ) : (
              !isCollapsed && (
                <CompanionConsentModal onDismiss={collapseSidebar} />
              )
            )}
          </div>
        </div>

        <div className={`flex ${isCollapsed && "invisible max-w-0"}`}>
          <NewSessionButton />
        </div>

        <div
          ref={handleRef}
          onPointerDown={onHandlePointerDown}
          onClick={(e) => {
            e.stopPropagation();
            if (dragOccurredRef.current) return;
            if (isCollapsed) {
              setWidth(SIDEBAR_DEFAULT_WIDTH);
            } else {
              setWidth(SIDEBAR_COLLAPSED_WIDTH);
            }
          }}
          className="absolute -right-1 top-0 h-full cursor-col-resize select-none touch-none flex items-center justify-center z-55 w-4"
          data-testid="companion-resize-handle"
        >
          <div className="mr-2 ">
            <FontAwesomeIcon
              icon={faGripLinesVertical}
              className={`text-white text-xl ${
                isCollapsed && "animate-pulse"
              } `}
            />
          </div>
        </div>
      </div>
    </CompanionModalContainerProvider>
  );
};
